import {
  huggg as hugggApi,
  superhuggg as superhugggApi,
  products as productApi,
  utils,
} from '@huggg/back-bar';
import config from 'config';
import { path, pathOr } from 'ramda';
import { areAllSegmentsOutOfStock, transformHuggg } from 'utilities/huggg';
import { getUrlParameter } from 'utilities/url';
import { isVirginExperienceDaysVoucher } from 'utilities/virginExperience';
import initialStateLoader from '../initialState';
import { UPDATE_BRAND } from './brand';
import { UPDATE_REACTION } from './reaction';

export const LOAD = 'huggg/huggg/LOAD';
export const LOAD_SUCCESS = 'huggg/huggg/LOAD_SUCCESS';
export const LOAD_FAILURE = 'huggg/huggg/LOAD_FAILURE';
export const UPDATE = 'huggg/huggg/UPDATE';
export const CLEAR = 'huggg/huggg/CLEAR';
export const SHOW_DETAILS = 'huggg/huggg/SHOW_DETAILS';
export const UNWRAPPED = 'huggg/huggg/UNWRAPPED';
export const REDEEMED_PRODUCT_LOAD = 'huggg/huggg/REDEEMED_PRODUCT_LOAD';
export const REDEEMED_PRODUCT_LOAD_SUCCESS =
  'huggg/huggg/REDEEMED_PRODUCT_LOAD_SUCCESS';
export const REDEEMED_PRODUCT_LOAD_FAILURE =
  'huggg/huggg/REDEEMED_PRODUCT_LOAD_FAILURE';

// Redeemed Product
let hasMessage;
let fetchMethod;

const showPageDetails = (huggg) => {
  if (
    config.enableMessageAnimation === 'false' ||
    getUrlParameter('disableMessageAnimation') === 'true' ||
    !hasMessage(huggg)
  ) {
    return true;
  }

  return false;
};

const isSuper = () => /^\/super\/.+/.test(window.location.pathname);
const isTest = () => /^\/test\/.+/.test(window.location.pathname);

const getDefaultState = () => {
  const { huggg } = initialStateLoader();

  if (isSuper()) {
    fetchMethod = (id, params) =>
      superhugggApi.single(id, {
        ...params,
        embed: [
          'sender',
          'purchase',
          'purchase.brand',
          'purchase.brand.stores',
        ],
        disableFormatting: true,
      });
    hasMessage = (huggg) => huggg && huggg.message;
  } else if (isTest()) {
    fetchMethod = hugggApi.testHuggg;
    hasMessage = (huggg) => Boolean(path(['message', 'message'], huggg));
  } else {
    fetchMethod = hugggApi.single;
    hasMessage = (huggg) => Boolean(path(['message', 'message'], huggg));
  }

  return {
    loading: false,
    showDetails: showPageDetails(huggg), // don't show details on load if there is a message. Wait for dispatch
    huggg: { ...huggg, isSuperhuggg: isSuper() },
    redeemedProduct: null,
    redeemedProductLoading: false,
  };
};

const reducer = (state = {}, { type, payload }) => {
  state = { ...getDefaultState(), ...state };

  switch (type) {
    case LOAD:
      return { ...state, loading: true };

    case LOAD_SUCCESS:
      return {
        ...state,
        huggg: { ...state.huggg, ...payload.huggg },
        showDetails: payload.showDetails,
        loading: false,
      };

    case LOAD_FAILURE:
      return { ...state, error: payload.error, loading: false };

    case UPDATE:
      return { ...state, huggg: { ...state.huggg, ...payload } };

    case CLEAR:
      return {
        ...state,
        huggg: { isSuperhuggg: isSuper() },
        redeemedProduct: null,
      };

    case REDEEMED_PRODUCT_LOAD:
      return { ...state, redeemedProductLoading: true };

    case REDEEMED_PRODUCT_LOAD_SUCCESS:
      return {
        ...state,
        redeemedProduct: payload.product,
        redeemedProductLoading: false,
      };

    case REDEEMED_PRODUCT_LOAD_FAILURE:
      return { ...state, redeemedProductLoading: false };

    case SHOW_DETAILS:
      return { ...state, showDetails: true };

    case UNWRAPPED:
      return { ...state, unwrapped: true };

    default:
      return state;
  }
};

export default reducer;

/**
 * SIDE EFFECTS
 */
const isPreview = getUrlParameter('preview') === 'true';
const throttled = {};
const fetchThrottled = (shortcode) => {
  throttled[shortcode] = throttled[shortcode]
    ? throttled[shortcode]
    : utils.throttlePromise(() => fetchMethod(shortcode, { isPreview }));

  return throttled[shortcode];
};

/**
 * Send Huggg QR Code via email
 *
 * @param {string} hugggId - The ID of the Huggg
 * @param {string} email - The recipient's email
 */
export const sendHugggQREmail = (hugggId, email) => () => {
  return hugggApi
    .sendHugggQREmail(hugggId, email)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      console.error('Error sending QR Code:', error);
      throw error;
    });
};

// This is called in withHuggg HoC IF we don't already have a huggg in state
// We could already have a huggg in state, if we're transitioning page or
// it has been injected into the page by the server in the window.hugggState function
// and then loaded in the initialState.js file
export const fetchHuggg = (shortcode) => (dispatch, getState) => {
  if (isFetching(getState())) {
    return;
  }

  dispatch({ type: LOAD });

  return fetchThrottled(shortcode)()
    .then((data) => {
      dispatch({
        type: UPDATE_BRAND,
        payload: {
          brand: pathOr({}, ['purchase', 'brand'], data),
          locations: pathOr([], ['purchase', 'brand', 'locations'], data),
        },
      });

      dispatch({
        type: UPDATE_REACTION,
        payload: pathOr({}, ['reaction'], data),
      });

      dispatch({
        type: LOAD_SUCCESS,
        payload: {
          huggg: transformHuggg(data).huggg,
          showDetails: showPageDetails(data),
        },
      });

      dispatch(fetchRedeemedProduct());

      return data;
    })
    .catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        throw err;
      }

      dispatch({
        type: LOAD_FAILURE,
        payload: { error: err },
      });
    });
};

export const fetchRedeemedProduct = () => (dispatch, getState) => {
  const productId = getRedeemedProductId(getState());
  if (!productId) {
    return Promise.resolve();
  }

  const existing = getRedeemedProduct(getState());
  if (existing && existing.id === productId) {
    return Promise.resolve();
  }

  dispatch({ type: REDEEMED_PRODUCT_LOAD });

  return productApi
    .single(productId)
    .then((product) => {
      dispatch({
        type: REDEEMED_PRODUCT_LOAD_SUCCESS,
        payload: { product },
      });
      return product;
    })
    .catch((error) => {
      console.error('Error fetching redeemed product:', error);
      dispatch({ type: REDEEMED_PRODUCT_LOAD_FAILURE });
    });
};

export const redeemTestHuggg = () => (dispatch) =>
  dispatch({ type: UPDATE, payload: { is_test_redeemed: true } });

/**
 * ACTIONS
 */
export const showDetails = () => (dispatch) =>
  dispatch({ type: SHOW_DETAILS, payload: {} });

export const clearHuggg = () => (dispatch) => dispatch({ type: CLEAR });

export const setHugggUnwrapped = () => (dispatch) => {
  return dispatch({ type: UNWRAPPED, payload: {} });
};

/**
 * SELECTORS
 */
export const getHuggg = ({ huggg }) => huggg.huggg || null;
export const getFetchError = (state) => state.huggg.error || null;

export const shouldShowDetails = ({ huggg }) =>
  huggg.showDetails && !!huggg.huggg.id;

export const getCode = ({ huggg }) => pathOr('', ['huggg', 'code'], huggg);

export const getLocations = (state) =>
  pathOr([], ['huggg', 'huggg', 'purchase', 'brand', 'locations'], state);

export const getRedemption = ({ huggg }) => huggg.redemption;

export const getReceiver = (state) =>
  pathOr(null, ['huggg', 'huggg', 'receiver'], state);

export const getSender = (state) =>
  pathOr(null, ['huggg', 'huggg', 'sender'], state);

export const getSegments = (state) =>
  pathOr([], ['huggg', 'huggg', 'purchase', 'segments'], state);

export const getMultiSegmentFlow = ({ huggg }) =>
  huggg.huggg?.productVoucherConfiguration?.flow ?? null;

export const getGuidedChoiceConfig = ({ huggg }) =>
  huggg.huggg?.productVoucherConfiguration?.guidedChoice?.choices ?? null;

export const isHugggUnwrapped = (state) => state?.huggg?.unwrapped ?? false;

export const getVouchers = (state) =>
  pathOr(null, ['huggg', 'huggg', 'vouchers'], state);

export const getRedeemedSegment = (state) =>
  pathOr(null, ['huggg', 'huggg', 'redeemedSegment'], state);

export const getRedeemedProductId = (state) =>
  pathOr(
    null,
    ['huggg', 'huggg', 'redeemedSegment', 'redemptionProductId'],
    state,
  ) || pathOr(null, ['huggg', 'huggg', 'redeemed', 'product_id'], state);

export const getRedeemedProduct = (state) =>
  pathOr(null, ['huggg', 'redeemedProduct'], state);

// Returns true while the redeemed product is being fetched
export const isLoadingRedeemedProduct = (state) =>
  pathOr(false, ['huggg', 'redeemedProductLoading'], state);

export const getVoucherExpiry = (state) =>
  isSegmentVoucher(state)
    ? pathOr(null, ['huggg', 'huggg', 'voucher', 'expiry_date'], state)
    : pathOr(null, ['huggg', 'huggg', 'vouchers', 0, 'expiresAt'], state);

// TODO: rename isBlueHuggg or refactor usage to base it on branding/theming
export const isBlueHuggg = (state) =>
  [
    process.env.REACT_APP_BLUE_CLIENT_ID,
    process.env.REACT_APP_BLUE_API_CLIENT_ID,
    process.env.REACT_APP_POST_OFFICE_CLIENT_ID,
    process.env.REACT_APP_POST_OFFICE_API_CLIENT_ID,
  ].includes(pathOr(null, ['huggg', 'huggg', 'purchase_client_id'], state));

export const isVirginBrandProduct = (state) =>
  pathOr(null, ['huggg', 'huggg', 'purchase', 'brand', 'id'], state) ===
  config.virginBrandId;

export const hasDeliveredSegment = (state) =>
  getSegments(state).some(
    (segment) => segment.voucherType === 'form-redemption',
  );

export const isExpired = (state) => {
  const hugggExpired = pathOr(false, ['huggg', 'huggg', 'is_expired'], state);
  const voucherExpiresAt = getVoucherExpiry(state);

  if (!voucherExpiresAt) {
    return hugggExpired;
  }

  // If there is a voucher expiry date, regardless of huggg expiry
  // We should check the voucher expiry
  const voucherExpired = new Date(voucherExpiresAt) <= new Date();
  return voucherExpired;
};

export const isFetching = (state) => state.huggg.loading || false;

export const isFormRedeemed = (state) =>
  Boolean(
    pathOr(false, ['huggg', 'huggg', 'vouchers', 0, 'voucherType'], state) ===
    'form-redemption',
  );

export const isStashed = (state) =>
  Boolean(pathOr(false, ['huggg', 'huggg', 'receiver_id'], state));

export const isVoucherV2 = (state) =>
  pathOr(
    0,
    ['huggg', 'huggg', 'purchase', 'brand', 'integration_id'],
    state,
  ) === 7;

export const hasVoucherToClaim = (state) => {
  const vouchers = pathOr([], ['huggg', 'huggg', 'vouchers'], state);

  return (
    isVoucherV2(state) && vouchers.some((voucher) => voucher.type === 'claim')
  );
};

export const hasFulfilledVoucher = (state) => {
  const vouchers = pathOr(
    [],
    ['huggg', 'huggg', 'vouchers', 0, 'fulfilmentStatus'],
    state,
  );

  return isVoucherV2(state) && vouchers === 'fulfilled';
};

export const isOutOfStock = (state) => {
  if (pathOr(false, ['huggg', 'huggg', 'purchase', 'out_of_stock'], state)) {
    return true;
  }

  // The product itself can still be sellable while every segment on it is out
  // of stock, which leaves the recipient with nothing to choose.
  return areAllSegmentsOutOfStock(path(['huggg', 'huggg'], state));
};

export const isRedeemed = (state) =>
  !hasVoucherToClaim(state) &&
  Boolean(
    path(['huggg', 'redemption', 'id'], state) ||
    path(['huggg', 'huggg', 'redeemed_id'], state),
  );

export const isSegmentVoucher = ({ huggg }) =>
  pathOr(0, ['huggg', 'purchase', 'brand', 'integration_id'], huggg) === 4;

export const isMultiRedeemable = (state) => {
  const isMultiRedeemableVoucher =
    ['mbls', 'tillo', 'wegift', 'stored', 'prepaid-card'].includes(
      pathOr(false, ['huggg', 'huggg', 'vouchers', 0, 'voucherType'], state),
    ) || isSegmentVoucher(state);

  return isMultiRedeemableVoucher || isVirginExperience(state);
};

export const isVirginExperience = (state) => {
  const redeemedSegment = getRedeemedSegment(state);
  const redeemedProduct = getRedeemedProduct(state);

  return isVirginExperienceDaysVoucher(redeemedSegment, redeemedProduct);
};

export const hasPrepaidCardVoucher = (state) =>
  pathOr([], ['huggg', 'huggg', 'vouchers'], state).some(
    (voucher) => voucher.voucherType === 'prepaid-card',
  );

export const isTestHuggg = ({ huggg }) => Boolean(huggg.huggg.is_test);

export const isTestHugggRedeemed = ({ huggg }) =>
  isTestHuggg({ huggg }) && Boolean(huggg.huggg.is_test_redeemed);

export const getError = ({ huggg }) => huggg.error;

// @todo change all refs in the app to this from utilities
export const isSuperhuggg = (state) =>
  pathOr(false, ['huggg', 'huggg', 'isSuperhuggg'], state);

export const isSuperhugggEmailClaim = (state) =>
  isSuperhuggg(state) &&
  pathOr(false, ['huggg', 'huggg', 'claim_method'], state) === 'email';

export const isSuperhugggSMSClaim = (state) =>
  isSuperhuggg(state) &&
  pathOr(false, ['huggg', 'huggg', 'claim_method'], state) === 'sms';

/**
 * Used for making sure that a superhuggg is a valid superhuggg
 *
 * @param {Object} state
 */
export const isValid = (state) =>
  pathOr(true, ['huggg', 'huggg', 'is_claimable'], state);

export const isCancelled = (state) =>
  Boolean(pathOr(false, ['huggg', 'huggg', 'deleted_at'], state));

export const shouldHideProductImageOnVoucherClaimed = (state) => {
  const vouchers = getVouchers(state);

  return vouchers.length === 1 && vouchers[0].voucherType === 'prepaid-card';
};

export const isFetchedHuggg = (state) =>
  pathOr(false, ['huggg', 'huggg', 'status_id'], state) === 9;
